import { RESPONSE_STATUS } from '@app/enums/new-relic.enum';
import { DataSaleInt } from '@app/interfaces/payment.interface';

/** Motivos definitivos del pinpad (CI-22236). `UNKNOW` respeta el contrato existente del driver. */
export const PINPAD_REASONS = ['APPROVED', 'DECLINED', 'FAILURE', 'CANCEL', 'UNKNOW'] as const;

export type PinpadReason = (typeof PINPAD_REASONS)[number];

/** Datos funcionales del intento de pago, solo primitivos. */
export interface PinpadAttemptData {
  amount: number | null;
  flowSource: string | null;
  itemsCount: number | null;
}

/** Atributos funcionales del evento New Relic `pinpad_response` (sin atributos globales). */
export interface PinpadResponsePayload {
  status: RESPONSE_STATUS;
  reason: PinpadReason;
  card_brand: string | null;
  amount: number | null;
  flow_source: string | null;
  items_count: number | null;
  interaction_time_ms: number;
  response_time_ms: number | null;
}

/**
 * Reduce cualquier mensaje técnico del dispositivo o del simulador a un motivo definitivo.
 * Coincidencia exacta (trim + mayúsculas) contra `PINPAD_REASONS`; cualquier otro valor cae en `UNKNOW`.
 */
export function normalizePinpadReason(raw: unknown): PinpadReason {
  const normalized = String(raw).trim().toUpperCase();
  const match = PINPAD_REASONS.find((reason) => reason === normalized);
  return match ?? 'UNKNOW';
}

/**
 * Milisegundos enteros >= 0 entre dos marcas de la misma fuente de tiempo.
 * Devuelve `null` (nunca `0`) cuando la marca de inicio no existe.
 */
export function elapsedMs(startedAt: number | null | undefined, endedAt: number): number | null {
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) {
    return null;
  }
  return Math.max(0, Math.round(endedAt - startedAt));
}

/** Lee únicamente `CBRAND` del resultado de venta, saneado a string no vacío o `null`. */
export function readCardBrand(sale: Partial<DataSaleInt> | null | undefined): string | null {
  const brand = sale?.CBRAND;
  if (typeof brand !== 'string') {
    return null;
  }
  const trimmed = brand.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Construye el payload funcional con las 8 claves `snake_case` y solo valores primitivos o `null`. */
export function buildPinpadResponsePayload(input: {
  status: RESPONSE_STATUS;
  reason: PinpadReason;
  cardBrand: string | null;
  attempt: PinpadAttemptData;
  interactionMs: number;
  responseMs: number | null;
}): PinpadResponsePayload {
  return {
    status: input.status,
    reason: input.reason,
    card_brand: input.cardBrand ?? null,
    amount:
      typeof input.attempt?.amount === 'number' && Number.isFinite(input.attempt.amount)
        ? input.attempt.amount
        : null,
    flow_source:
      typeof input.attempt?.flowSource === 'string' && input.attempt.flowSource.trim().length > 0
        ? input.attempt.flowSource
        : null,
    items_count:
      typeof input.attempt?.itemsCount === 'number' && Number.isFinite(input.attempt.itemsCount)
        ? input.attempt.itemsCount
        : null,
    interaction_time_ms: input.interactionMs,
    response_time_ms: input.responseMs ?? null,
  };
}
